import React from "react";
import { cn } from "../../lib/utils";
import type { MapViewerControlClusterProps } from "../../contracts/map-viewer";

/**
 * MapViewerControlCluster is a compact icon utility group for floating map controls.
 * It supports active states, badges, and different tones for actions like filters, layers, and legend.
 */
export function MapViewerControlCluster({
  items,
  onAction,
  className,
}: MapViewerControlClusterProps) {
  return (
    <nav
      className={cn(
        "flex flex-col gap-1 rounded-lg border border-border bg-card/92 p-1 shadow-lg backdrop-blur-sm",
        className
      )}
      aria-orientation="vertical"
    >
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={() => !item.disabled && onAction(item.id)}
          disabled={item.disabled}
          title={item.label}
          aria-label={item.label}
          aria-pressed={item.active}
          className={cn(
            "relative flex h-8 w-8 items-center justify-center rounded-md transition-colors",
            "hover:bg-accent hover:text-accent-foreground",
            "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
            "disabled:pointer-events-none disabled:opacity-40",
            item.active
              ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm"
              : item.tone === "success"
                ? "text-primary"
                : item.tone === "warning"
                  ? "text-foreground"
                  : "text-muted-foreground"
          )}
        >
          {item.icon}

          {item.badge && (
            <span
              className={cn(
                "absolute -right-0.5 -top-0.5 flex h-3.5 min-w-[0.875rem] items-center justify-center rounded-full px-1 text-[8px] font-bold ring-1 ring-card",
                item.tone === "success" && "bg-primary text-primary-foreground",
                item.tone === "warning" && "bg-accent text-accent-foreground",
                (!item.tone || item.tone === "default") && "bg-foreground text-background"
              )}
            >
              {item.badge}
            </span>
          )}

          <span className="sr-only">{item.label}</span>
        </button>
      ))}
    </nav>
  );
}
